// FILE: threadDirectory.ts
// Purpose: Schemas for the thread directory — the paged, filterable listing of every root thread
//          in the installation, gated by `threads.view_all`.
// Layer: Shared contracts (schema-only)
// Depends on: base schemas and `PullRequestRef`.
//
// What the sidebar shows and what this answers are now two different questions. The sidebar is
// membership: threads you created or were given. This is the whole installation, and it is a
// *search*, not a feed — nothing here is pushed, nothing here subscribes, and opening a row does
// not put it in anybody's sidebar. That separation is the point: an administrator who works their
// own threads should not have to read past everyone else's to find them.
//
// Root threads only. Delegations and side chats are threads in their own right but they belong to
// a conversation, and listing them here would answer "what is happening in this installation" with
// a machine's bookkeeping rather than with the work people started.

import { Schema } from "effect";

import {
  IsoDateTime,
  ManagedAgentId,
  PositiveInt,
  ProjectId,
  ThreadId,
  UserId,
} from "./baseSchemas";
import { ProviderKind } from "./orchestration";
import { PullRequestRef } from "./pullRequest";

/**
 * Which threads the state filter keeps.
 *
 *  - `active`   — not archived. The default, and the only one most reads want.
 *  - `archived` — archived, and nothing else.
 *  - `running`  — a turn is in flight right now, archived or not. This is the "what is the
 *                 installation doing at this instant" read, and it is why the filter is not a
 *                 plain archived/not-archived boolean.
 *  - `all`      — no state narrowing at all.
 */
export const ThreadDirectoryState = Schema.Literals(["active", "archived", "running", "all"]);
export type ThreadDirectoryState = typeof ThreadDirectoryState.Type;

/** Newest activity first, or newest creation first. Both descending — a directory is read from the top. */
export const ThreadDirectorySort = Schema.Literals(["activity", "created"]);
export type ThreadDirectorySort = typeof ThreadDirectorySort.Type;

/**
 * Free text, matched against the thread title *and* the text of its messages.
 *
 * Word-based, not substring: the index behind it is a Postgres full-text index, so "deploy" finds
 * "deploy" and not "redeployment". Substring matching over every message ever written would be a
 * sequential scan of the largest table we have, on every keystroke.
 */
export const THREAD_DIRECTORY_QUERY_MAX_LENGTH = 200;

export const ThreadDirectoryFilters = Schema.Struct({
  query: Schema.optional(
    Schema.String.check(Schema.isMaxLength(THREAD_DIRECTORY_QUERY_MAX_LENGTH)),
  ),
  /** One project. Absent reads every project. */
  projectId: Schema.optional(ProjectId),
  /** One person, matched as the thread's author *or* as somebody it was shared with. */
  userId: Schema.optional(UserId),
  state: ThreadDirectoryState.pipe(Schema.withDecodingDefault(() => "active" as const)),
  /** True keeps only threads with a linked pull request; false keeps only those without. */
  hasPullRequest: Schema.optional(Schema.Boolean),
  /** Inclusive bounds on last activity, as ISO instants. */
  activityFrom: Schema.optional(IsoDateTime),
  activityTo: Schema.optional(IsoDateTime),
  /** Inclusive bounds on creation, as ISO instants. */
  createdFrom: Schema.optional(IsoDateTime),
  createdTo: Schema.optional(IsoDateTime),
});
export type ThreadDirectoryFilters = typeof ThreadDirectoryFilters.Type;

export const THREAD_DIRECTORY_MAX_PER_PAGE = 100;
export const THREAD_DIRECTORY_DEFAULT_PER_PAGE = 25;

/**
 * Page-based paging rather than a keyset cursor, for the reason `PullRequestListInput` gives and
 * one of its own: a directory is walked ("page 4 of 12"), not scrolled, and the filters narrow the
 * set enough that an offset stays cheap. If a page ever has to survive rows being inserted while
 * somebody reads it, that is when this becomes a cursor.
 */
export const ThreadDirectoryInput = Schema.Struct({
  filters: ThreadDirectoryFilters,
  sort: ThreadDirectorySort.pipe(Schema.withDecodingDefault(() => "activity" as const)),
  /** 1-based. */
  page: PositiveInt.pipe(Schema.withDecodingDefault(() => 1)),
  perPage: PositiveInt.check(Schema.isLessThanOrEqualTo(THREAD_DIRECTORY_MAX_PER_PAGE)).pipe(
    Schema.withDecodingDefault(() => THREAD_DIRECTORY_DEFAULT_PER_PAGE),
  ),
});
export type ThreadDirectoryInput = typeof ThreadDirectoryInput.Type;

/**
 * One row of the directory.
 *
 * Deliberately not an `OrchestrationThread`: that carries the messages, activities, plans and
 * session state a chat surface needs, and a page of 25 of those is most of what the shell snapshot
 * used to cost. This is what a row draws and nothing else.
 */
export const ThreadDirectoryEntry = Schema.Struct({
  threadId: ThreadId,
  projectId: ProjectId,
  title: Schema.String,
  createdAt: IsoDateTime,
  /** Last activity of any kind, which is what the default sort orders by. */
  updatedAt: IsoDateTime,
  /** Null on threads created before authorship was recorded, and on installs without auth. */
  createdBy: Schema.NullOr(UserId),
  assignedUserIds: Schema.Array(UserId),
  archivedAt: Schema.NullOr(IsoDateTime),
  linkedPullRequest: Schema.NullOr(PullRequestRef),
  /** A turn is in flight right now. */
  isRunning: Schema.Boolean,
  /**
   * What runs the thread: the provider, the model slug, and the managed agent when one is applied.
   *
   * This is the thread's *current* selection — the one the composer shows and the one the next turn
   * would use — not a history of everything it ever ran under. A thread that was moved from Sonnet
   * to Opus halfway through reads as Opus here, which is the answer somebody scanning the directory
   * for "what is this running on" wants; per-turn attribution already lives in Usage, where a
   * breakdown belongs.
   *
   * All three are nullable together: a thread whose `model_selection_json` is missing or unreadable
   * degrades to "unknown" rather than failing the page, for the same reason the pull request link
   * does — this is the screen an operator opens *because* something looks wrong.
   */
  provider: Schema.NullOr(ProviderKind),
  model: Schema.NullOr(Schema.String),
  /**
   * The managed agent applied to the session, resolved to its name on the server.
   *
   * Resolved here and not in the client because the reader of this section sees threads run by
   * agents outside their own allowlist, which is exactly what the client's agent list omits.
   */
  agent: Schema.NullOr(
    Schema.Struct({
      agentId: ManagedAgentId,
      name: Schema.String,
    }),
  ),
  /**
   * The matching fragment, when the read carried a query. Comes from the message that matched,
   * with the matched words marked by `«` and `»` — chosen over HTML because the client renders
   * it as text, and a headline carrying markup into a React tree is an injection waiting to
   * happen.
   */
  snippet: Schema.optional(Schema.String),
});
export type ThreadDirectoryEntry = typeof ThreadDirectoryEntry.Type;

export const ThreadDirectoryResult = Schema.Struct({
  entries: Schema.Array(ThreadDirectoryEntry),
  /** Echoed so a late answer cannot be drawn as if it were the page now on screen. */
  page: PositiveInt,
  perPage: PositiveInt,
  /** Rows matching the filters, across every page. What "página 3 de 12" counts. */
  total: Schema.Int,
  hasMore: Schema.Boolean,
});
export type ThreadDirectoryResult = typeof ThreadDirectoryResult.Type;
